/**
 * Cliente de la API.
 *
 * Toda peticion va con credenciales, porque la identidad viaja en la cookie de
 * sesion. El token del QR no se envia nunca en el cuerpo ni en la URL.
 */

import type { CodigoError, ErrorApi, Idioma } from '@recorregut/shared';
import { idiomaActivo } from '@/i18n';

export class ErrorRespuesta extends Error {
  readonly codigo: CodigoError;
  readonly estado: number;
  readonly contexto: Record<string, string | number> | undefined;

  constructor(codigo: CodigoError, estado: number, contexto?: Record<string, string | number>) {
    super(codigo);
    this.name = 'ErrorRespuesta';
    this.codigo = codigo;
    this.estado = estado;
    this.contexto = contexto;
  }
}

/** Fallo de red o servidor inalcanzable, distinto de un error de negocio. */
export class ErrorRed extends Error {
  constructor() {
    super('xarxa');
    this.name = 'ErrorRed';
  }
}

interface OpcionesPeticion {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  cuerpo?: unknown;
  idioma?: Idioma;
  senal?: AbortSignal;
}

function construirUrl(ruta: string, idioma: Idioma): string {
  const url = new URL(ruta, window.location.origin);
  // El idioma va en la consulta para que el contenido llegue ya resuelto y
  // para que las respuestas se puedan cachear por idioma.
  url.searchParams.set('idioma', idioma);
  return url.pathname + url.search;
}

export async function peticion<T>(ruta: string, opciones: OpcionesPeticion = {}): Promise<T> {
  const idioma = opciones.idioma ?? idiomaActivo();
  const metodo = opciones.metodo ?? 'GET';

  let respuesta: Response;
  try {
    respuesta = await fetch(construirUrl(ruta, idioma), {
      method: metodo,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Accept-Language': idioma,
        ...(opciones.cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(opciones.cuerpo !== undefined ? { body: JSON.stringify(opciones.cuerpo) } : {}),
      ...(opciones.senal ? { signal: opciones.senal } : {}),
    });
  } catch {
    // fetch solo rechaza por fallo de red, no por codigo de estado.
    throw new ErrorRed();
  }

  if (respuesta.status === 204) return undefined as T;

  const tipo = respuesta.headers.get('content-type') ?? '';
  const esJson = tipo.includes('application/json');
  const datos: unknown = esJson ? await respuesta.json() : await respuesta.text();

  if (!respuesta.ok) {
    const error = datos as ErrorApi;
    throw new ErrorRespuesta(
      error?.error ?? 'error_interno',
      respuesta.status,
      error?.contexto,
    );
  }

  return datos as T;
}

export const api = {
  get: <T>(ruta: string, senal?: AbortSignal): Promise<T> =>
    peticion<T>(ruta, senal ? { senal } : {}),
  post: <T>(ruta: string, cuerpo?: unknown): Promise<T> =>
    peticion<T>(ruta, { metodo: 'POST', ...(cuerpo !== undefined ? { cuerpo } : {}) }),
  patch: <T>(ruta: string, cuerpo: unknown): Promise<T> =>
    peticion<T>(ruta, { metodo: 'PATCH', cuerpo }),
  put: <T>(ruta: string, cuerpo: unknown): Promise<T> =>
    peticion<T>(ruta, { metodo: 'PUT', cuerpo }),
  borrar: <T>(ruta: string): Promise<T> => peticion<T>(ruta, { metodo: 'DELETE' }),
};
